import { useState, useEffect } from 'react';
import { useAgencyAuth } from "../../contexts/AgencyAuthContext";
import { agencyService } from "../../services/agency.service";
import { toast } from "react-hot-toast";
import { Card, CardContent } from '@/components/ui/card';
import { Users, TrendingUp, CreditCard } from 'lucide-react';
import { AgencyCodeModal } from "../../components/agency/AgencyCodeModal";
import { ReferralLinkCard } from "../../components/agency/ReferralLinkCard";
import { TopBrandsCard } from "../../components/agency/TopBrandsCard";
import PageHeader from "../../components/common/PageHeader";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export function AgencyDashboard() {
  const { user, profile, updateProfile } = useAgencyAuth();
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [topBrands, setTopBrands] = useState<any[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [availablePayout, setAvailablePayout] = useState(0);

  useEffect(() => {
    // First-earning nudge data: is there commission waiting?
    agencyService
      .getPayoutStatus()
      .then((s) => setAvailablePayout(s.availableAmount || 0))
      .catch(() => setAvailablePayout(0));
  }, []);

  useEffect(() => {
    // Check if agency has a code, if not show modal
    if (profile && !profile.code) {
      setShowCodeModal(true);
    }
  }, [profile]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch summary data
      const summary = await agencyService.getReferredBrandsSummary();
      setSummaryData(summary);

      // Fetch top brands
      const brandsResponse = await agencyService.getReferredBrandsTable(1, 5);
      setTopBrands(brandsResponse.brands);

      // Fetch analytics data for clicks and conversions
      try {
        const analytics = await agencyService.getAnalytics();
        setAnalyticsData(analytics);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        // Don't fail the entire dashboard if analytics fails
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSuccess = (code: string) => {
    if (profile) {
      updateProfile({
        ...profile,
        code,
      });
    }
    setShowCodeModal(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Extract clicks and conversions from analytics data
  const totalClicks = analyticsData?.performanceMetrics?.totalClicks?.value || 0;
  const conversions = analyticsData?.performanceMetrics?.conversions?.value || 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title=""
          description="Here's your agency performance overview"
          username={user?.firstName}
        />
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title=""
        description="Here's your agency performance overview"
        username={user?.firstName}
      />

      {/* First-earning nudge: commission is waiting and no payout method is
          saved. Payout details are only required at the request itself. */}
      {availablePayout > 0 && profile && (profile.paymentMethods?.length ?? 0) === 0 && (
        <div
          className="flex flex-col gap-3 rounded-xl border border-brand-600/20 bg-brand-600/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          role="status"
        >
          <div>
            <p className="text-sm font-medium text-foreground">
              You've earned {formatCurrency(availablePayout)} in commission. Tell us where to send it.
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Add one payout method and you can request it any time.
            </p>
          </div>
          <Button asChild size="sm">
            <Link to="/agency/payouts">Add payout method</Link>
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Brands</p>
                <p className="text-2xl font-bold text-foreground">{summaryData?.totalBrands || 0}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <Users className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Brands</p>
                <p className="text-2xl font-bold text-foreground">{summaryData?.activeBrands || 0}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">My Commission</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryData?.totalCommission || 0)}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <CreditCard className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referral Link Card */}
        {profile?.code ? (
          <ReferralLinkCard
            referralCode={profile.code}
            totalClicks={totalClicks}
            conversions={conversions}
          />
        ) : (
          <div className="bg-card rounded-lg shadow p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                My Referral Link
              </h3>
              <p className="text-sm text-muted-foreground">
                Set your agency code to get your referral link
              </p>
            </div>
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <p className="text-muted-foreground mb-2">No referral link available</p>
              <p className="text-sm text-muted-foreground">
                Set your agency code to generate your referral link
              </p>
            </div>
          </div>
        )}

        {/* Top Brands Card */}
        <TopBrandsCard
          brands={topBrands}
          title="My Brands"
        />
      </div>

      {/* Agency Code Modal */}
      <AgencyCodeModal
        isOpen={showCodeModal}
        onClose={() => setShowCodeModal(false)}
        onSuccess={handleCodeSuccess}
        firstName={user?.firstName || ''}
      />
    </div>
  );
}